import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
// eslint-disable-next-line lodash/import-scope
import type { DebounceSettings } from 'lodash';
import type { ActionCreator, AnyAction } from 'redux';

import { useDebouncedCallback } from '../../../hooks';
import * as PackageInventoryStore from '../../../redux/packageInventory';

const DEBOUNCE_TIMEOUT = 750;

//  We want requests to happen after the last user action takes place.
//  Read more: https://css-tricks.com/debouncing-throttling-explained-examples/#aa-debounce
const DEBOUNCE_OPTIONS: DebounceSettings = {
  leading: false,
  trailing: true,
};

/**
 * Hook which will return a function which dispatches a given action
 * first, followed by dispatching a debounced version of the async fetchPackageProjects.
  * We have to define it here because we need easy access to the component's params.
  */
const useFetchPackageVersionProjects = (
  actionCreator: ActionCreator<AnyAction>,
  packageLocator: string,
  version: string,
  resetPage: boolean = false,
) => {
  const dispatch = useDispatch();

  //  Memoized debounce function, which re-fetches the packages.
  const dispatchFetchPackages = useDebouncedCallback(
    () => {
      dispatch(PackageInventoryStore.actions.fetchPackageProjects(packageLocator, version));
      dispatch(PackageInventoryStore.actions.fetchPackageVersionMetadata(packageLocator, version));
    },
    DEBOUNCE_TIMEOUT,
    DEBOUNCE_OPTIONS,
  );

  //  This helper does three things, in synchronous order:
  //  - dispatch an action which will update the state immediately in response to user action
  //  - after the debounce timeout, the packages are fetched
  const fetchPackageVersions = useCallback(
    (...args) => {
      if (resetPage) {
        dispatch(PackageInventoryStore.actions.resetVersionDetailsPage());
      }

      //  Dispatch the passed in action, which updates the Redux store immediately
      dispatch(actionCreator(...args));

      dispatchFetchPackages();
    },
    [actionCreator],
  );

  return fetchPackageVersions;
};

export default useFetchPackageVersionProjects;
